import React, { useState } from 'react';
import PickoffModal from './PickoffModal';
import { hasRISP, runnersOnCount, maxRBI } from '../../hooks/useGameState';

// ─────────────────────────────────────────────
// BASE STATE DIAMOND
// Used in AtBatSetup — OTHER runners for RISP/RBI context
// ─────────────────────────────────────────────

function BatCountDisplay({ balls, strikes, pitchCount, onBallTap, onStrikeTap, countEnabled, onOpenSettings }) {
  return (
    <div className="pt-count-bar">
      <div className="pt-count-block" style={{ cursor: 'pointer' }} onClick={onBallTap}>
        <div className="pt-count-number balls">{balls}</div>
        <div className="pt-count-label">BALLS</div>
        <div className="pt-dots-row">
          {[0,1,2,3].map(i => (
            <span key={i} className={`pt-dot ball-dot ${i < balls ? 'active' : ''}`} />
          ))}
        </div>
      </div>
      <div className="pt-count-center">
        {countEnabled ? (
          <>
            <div className="pt-pitch-count">{pitchCount} pitches</div>
            <div style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 4 }}>
              Tap to count
            </div>
          </>
        ) : (
          <button
            onClick={onOpenSettings}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: 'var(--text-3)', textAlign: 'center', lineHeight: 1.4, padding: '4px 8px', fontFamily: 'var(--font-ui)' }}
          >
            Tap to change<br />tracking options
          </button>
        )}
      </div>
      <div className="pt-count-block" style={{ cursor: 'pointer' }} onClick={onStrikeTap}>
        <div className="pt-count-number strikes">{strikes}</div>
        <div className="pt-count-label">STRIKES</div>
        <div className="pt-dots-row">
          {[0,1,2].map(i => (
            <span key={i} className={`pt-dot strike-dot ${i < strikes ? 'active' : ''}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function BaseDiamond({ baseState, onToggle, onOccupiedTap, interactive = false }) {
  const bases = [
    { key: 'second', label: '2B', cx: 50, cy: 12 },
    { key: 'third', label: '3B', cx: 12, cy: 50 },
    { key: 'first', label: '1B', cx: 88, cy: 50 },
  ];

  return (
    <svg
      viewBox="0 0 100 100"
      className={`base-diamond ${interactive ? 'interactive' : ''}`}
      aria-label="Base state"
    >
      <line
        x1="50"
        y1="12"
        x2="88"
        y2="50"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="88"
        y1="50"
        x2="50"
        y2="88"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="50"
        y1="88"
        x2="12"
        y2="50"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="12"
        y1="50"
        x2="50"
        y2="12"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <polygon points="50,82 44,88 50,94 56,88" fill="var(--border)" />
      {bases.map(({ key, cx, cy }) => (
        <g
          key={key}
          onClick={() => {
            if (!interactive) return;
            if (baseState[key]) {
              onOccupiedTap && onOccupiedTap(key);
            } else {
              onToggle && onToggle(key);
            }
          }}
          style={{ cursor: interactive ? 'pointer' : 'default' }}
        >
          <rect
            x={cx - 7}
            y={cy - 7}
            width="14"
            height="14"
            transform={`rotate(45 ${cx} ${cy})`}
            fill={baseState[key] ? 'var(--amber)' : 'var(--surface-2)'}
            stroke={baseState[key] ? 'var(--amber)' : 'var(--border-light)'}
            strokeWidth="1.5"
          />
        </g>
      ))}
    </svg>
  );
}

function BaserunnerActionModal({ base, onAdvance, onRemove, onClose }) {
  const baseLabel = base === 'first' ? '1st' : base === 'second' ? '2nd' : '3rd';
  const advanceLabel = base === 'third' ? 'Runner scored' : `Advance to ${base === 'first' ? '2nd' : '3rd'}`;
  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Runner on {baseLabel}</div>
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={onAdvance}>
            {advanceLabel}
          </button>
          <button className="fld-modal-confirm" onClick={onRemove}>
            Remove runner
          </button>
          <button className="fld-modal-cancel" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// PLAYER POSITION DIAMOND
// Tracks where MY PLAYER currently is on the bases.
// Only rendered when playerPosition is not null.
// Tapping a base ahead triggers AdvancementPrompt.
// Tapping home triggers ScorePrompt.
// ─────────────────────────────────────────────

function PlayerPositionDiamond({ position, onAdvanceTo }) {
  const BASE_ORDER = ['first', 'second', 'third'];
  const currentIdx = BASE_ORDER.indexOf(position);

  const bases = [
    { key: 'second', cx: 50, cy: 14 },
    { key: 'third', cx: 14, cy: 50 },
    { key: 'first', cx: 86, cy: 50 },
  ];

  return (
    <svg viewBox="0 0 100 100" className="player-pos-diamond">
      <line
        x1="50"
        y1="14"
        x2="86"
        y2="50"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="86"
        y1="50"
        x2="50"
        y2="86"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="50"
        y1="86"
        x2="14"
        y2="50"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />
      <line
        x1="14"
        y1="50"
        x2="50"
        y2="14"
        stroke="var(--border-light)"
        strokeWidth="1.5"
      />

      {/* Home plate — always tappable */}
      <polygon
        points="50,80 44,86 50,92 56,86"
        fill="var(--green-bg)"
        stroke="var(--green)"
        strokeWidth="1.5"
        style={{ cursor: 'pointer' }}
        onClick={() => onAdvanceTo('home')}
      />

      {bases.map(({ key, cx, cy }) => {
        const idx = BASE_ORDER.indexOf(key);
        const isOccupied = key === position;
        const isAdvanceable = currentIdx >= 0 && idx > currentIdx;
        const isCurrent = isOccupied;

        return (
          <g
            key={key}
            onClick={() => {
              if (isCurrent) onAdvanceTo(key);
              else if (isAdvanceable) onAdvanceTo(key);
            }}
            style={{
              cursor: isCurrent || isAdvanceable ? 'pointer' : 'default',
            }}
          >
            <rect
              x={cx - 8}
              y={cy - 8}
              width="16"
              height="16"
              transform={`rotate(45 ${cx} ${cy})`}
              fill={
                isOccupied
                  ? 'var(--amber)'
                  : isAdvanceable
                    ? 'rgba(232,200,74,0.12)'
                    : 'var(--surface)'
              }
              stroke={
                isOccupied
                  ? 'var(--amber)'
                  : isAdvanceable
                    ? 'var(--amber-dim)'
                    : 'var(--border)'
              }
              strokeWidth={isOccupied ? 2 : 1.5}
            />
            {isOccupied && <circle cx={cx} cy={cy} r="3.5" fill="var(--bg)" />}
          </g>
        );
      })}
    </svg>
  );
}

// ─────────────────────────────────────────────
// ADVANCEMENT PROMPT
// How did the player advance to the next base?
// ─────────────────────────────────────────────

function AdvancementPrompt({ from, to, onConfirm, onCancel }) {
  const L = { first: '1st', second: '2nd', third: '3rd' };
  const isNotable = from === 'first' && to === 'third';
  const [phase, setPhase] = useState('main');
  const mainOptions = [
    { key: 'advanced_on_hit', label: 'Advanced on Hit' },
    { key: 'advanced_on_error', label: 'Advanced on Error' },
    { key: 'advanced_on_wp_pb', label: 'Advanced on Wild Pitch / PB' },
    { key: 'tag_up', label: 'Tagged Up' },
    { key: 'attempted_steal', label: 'Attempted Steal' },
  ];
  const outOptions = [
    { key: 'fc_out', label: 'Fielder\'s Choice' },
    { key: 'thrown_out', label: 'Thrown / Tagged Out Advancing' },
    { key: 'failed_tag_up', label: 'Failed Tag Up' },
  ];
  return (
    <div className="fld-modal-overlay" onClick={onCancel}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        {phase === 'main' && (
          <>
            <div className="fld-modal-title">
              {L[from]} → {L[to]}
              {isNotable && <span style={{ color: 'var(--amber)', marginLeft: 8, fontSize: 12 }}>First to Third!</span>}
            </div>
            <div className="fld-modal-actions">
              {mainOptions.map(({ key, label }) => (
                <button key={key} className="fld-modal-confirm" onClick={() => onConfirm(key)}>{label}</button>
              ))}
              <button className="fld-modal-confirm" onClick={() => setPhase('runner_out')}>Runner Out</button>
              <button className="fld-modal-cancel" onClick={onCancel}>Cancel</button>
            </div>
          </>
        )}
        {phase === 'runner_out' && (
          <>
            <div className="fld-modal-title">Runner Out</div>
            <div className="fld-modal-actions">
              {outOptions.map(({ key, label }) => (
                <button key={key} className="fld-modal-confirm" onClick={() => onConfirm(key)}>{label}</button>
              ))}
              <button className="fld-modal-cancel" onClick={() => setPhase('main')}>← Back</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// OUT PROMPT
// Shown when tapping the base the player is currently on
// ─────────────────────────────────────────────

function OutPrompt({ base, onConfirm, onCancel }) {
  const L = { first: '1st', second: '2nd', third: '3rd' };
  const options = [
    { key: 'picked_off', label: 'Pickoff Attempt' },
  ];

  return (
    <div className="fld-modal-overlay" onClick={onCancel}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Out at {L[base]}?</div>
        <div className="fld-modal-actions">
          {options.map(({ key, label }) => (
            <button key={key} className="fld-modal-confirm" onClick={() => onConfirm(key)}>{label}</button>
          ))}
          <button className="fld-modal-cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SCORE PROMPT
// Player is heading home — did they score?
// ─────────────────────────────────────────────

function ScorePrompt({ onScore, onOut, onCancel }) {
  const options = [
    { key: 'advanced_on_hit', label: 'Scored on Hit' },
    { key: 'stolen_base', label: 'Steal of Home' },
    { key: 'tag_up', label: 'Scored on Tag Up' },
    { key: 'advanced_on_error', label: 'Scored on Error' },
    { key: 'advanced_on_wp_pb', label: 'Wild Pitch / PB' },
  ];

  return (
    <div className="fld-modal-overlay" onClick={onCancel}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Heading Home!</div>
        <div className="fld-modal-body">How did they score?</div>
        <div className="fld-modal-actions">
          {options.map(({ key, label }) => (
            <button
              key={key}
              className="fld-modal-confirm"
              onClick={() => onScore(key)}
            >
              {label}
            </button>
          ))}
          <button
            className="fld-modal-confirm"
            onClick={() => onOut(null)}
            style={{ background: '#c0001a', color: '#fff' }}
          >
            Out at Home
          </button>
          <button className="fld-modal-cancel" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// "NEW AT BAT" SETUP PANEL
// Parent sets other runners for RISP/RBI context
// ─────────────────────────────────────────────

function AtBatSetup({ game, onStart }) {
  const { state } = game;
  const prefs = state.batting.trackingPrefs;

  const [baseState, setBaseState] = useState({
    first: false,
    second: false,
    third: false,
  });
  const [outCount, setOutCount] = useState(0);

  function toggleBase(key) {
    setBaseState((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function togglePref(key) {
    game.setBattingTrackingPrefs({ [key]: !prefs[key] });
  }

  const risp = hasRISP(baseState);
  const runnerCount = runnersOnCount(baseState);

  return (
    <div className="bat-setup-panel">
      <div className="bat-setup-title">New At Bat</div>

      {/* Other runners diamond */}
      <div className="bat-setup-diamond-wrap">
        <BaseDiamond baseState={baseState} onToggle={toggleBase} interactive />
        <div className="bat-setup-diamond-hint">
          {runnerCount === 0 ? (
            <span>RUNNERS ON BASE? TAP EACH BASE TO SET THEM FOR TRACKING</span>
          ) : (
            <span className={risp ? 'risp-on' : ''}>
              {runnerCount} runner{runnerCount > 1 ? 's' : ''} on
              {risp ? ' · RISP' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="bat-setup-outs">
        <div className="bat-setup-prefs-label">Outs</div>
        <div className="bat-setup-outs-row">
          {[0, 1, 2].map(n => (
            <button
              key={n}
              className={`bat-out-btn ${outCount === n ? 'active' : ''}`}
              onClick={() => setOutCount(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      {/* Tracking prefs */}
      <div className="bat-setup-prefs">
        <div className="bat-setup-prefs-label">Track this at-bat</div>
        <div className="bat-setup-prefs-row">
          {[
            { key: 'count', label: 'Count' },
            { key: 'strikeDetail', label: 'Strike Type' },
            { key: 'hitType', label: 'Hit Type' },
            { key: 'hitLocation', label: 'Location' },
            { key: 'strikeoutType', label: 'K Type' },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`bat-pref-btn ${prefs[key] ? 'on' : 'off'}`}
              onClick={() => togglePref(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <button className="bat-start-ab-btn" onClick={() => onStart(baseState, outCount)}>
        Start At Bat →
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// RBI PROMPT
// ─────────────────────────────────────────────

function RBIPrompt({ outcome, baseState, onConfirm, onBack }) {
  const suggested = maxRBI(baseState, outcome);
  const [rbi, setRbi] = useState(
    outcome === 'hr' || outcome === 'iphr' ? suggested : 0
  );
  const options = Array.from({ length: suggested + 1 }, (_, i) => i);
  return (
    <div className="fld-modal-overlay">
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">RBI?</div>
        <div className="fld-modal-actions">
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 8 }}>
            {options.map((n) => (
              <button
                key={n}
                onClick={() => setRbi(n)}
                style={{
                  background: rbi === n ? 'var(--amber)' : 'var(--surface-2)',
                  color: rbi === n ? '#0d0f14' : 'var(--text)',
                  border: 'none', borderRadius: 8, padding: '10px 18px',
                  fontSize: 18, fontWeight: 700, cursor: 'pointer', minWidth: 48,
                }}
              >
                {n}
              </button>
            ))}
          </div>
          <button className="fld-modal-confirm" onClick={() => onConfirm(rbi)}>Confirm</button>
          {onBack && <button className="fld-modal-cancel" onClick={onBack}>← Back</button>}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// HIT TYPE PROMPT
// ─────────────────────────────────────────────

function HitTypePrompt({ onSelect, onBack }) {
  const types = ['Line Drive', 'Ground Ball', 'Fly Ball', 'Pop Up', 'Bunt'];
  return (
    <div className="fld-modal-overlay">
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Hit Type</div>
        <div className="fld-modal-actions">
          {types.map((t) => (
            <button key={t} className="fld-modal-confirm" onClick={() => onSelect(t)}>{t}</button>
          ))}
          {onBack && <button className="fld-modal-cancel" onClick={onBack}>← Back</button>}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// BALL IN PLAY RESULT PROMPT
// ─────────────────────────────────────────────
function ResultPrompt({ onSelect, onBack }) {
  return (
    <div className="fld-modal-overlay">
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Result</div>
        <div className="fld-modal-body" style={{ marginBottom: 4 }}>Hits</div>
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={() => onSelect('single')}>Single</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('double')}>Double</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('triple')}>Triple</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('hr')}>Home Run</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('iphr')}>Inside Park HR</button>
        </div>
        <div className="fld-modal-body" style={{ marginTop: 8, marginBottom: 4 }}>Out / Other</div>
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={() => onSelect('out')}>Out</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('roe')}>Reached on Error</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('fc')}>Fielder's Choice</button>
        </div>
        {onBack && <div className="fld-modal-actions" style={{ marginTop: 8 }}><button className="fld-modal-cancel" onClick={onBack}>← Back</button></div>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// OUT SUBTYPE PROMPT
// ─────────────────────────────────────────────
function OutSubtypePrompt({ hitType, runnersOn, onSelect, onBack }) {
  const isAirBall = ['Fly Ball', 'Pop Up', 'Line Drive'].includes(hitType);
  return (
    <div className="fld-modal-overlay">
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Out Type</div>
        <div className="fld-modal-actions">
          {isAirBall ? (
            <>
              <button className="fld-modal-confirm" onClick={() => onSelect('caught')}>Caught Out</button>
              <button className="fld-modal-confirm" onClick={() => onSelect('sf')}>Sac Fly</button>
            </>
          ) : (
            <>
              <button className="fld-modal-confirm" onClick={() => onSelect('out')}>Out at 1st</button>
              {hitType === 'Ground Ball' && runnersOn > 0 && (
                <button className="fld-modal-confirm" onClick={() => onSelect('gidp')}>Ground into Double Play</button>
              )}
              <button className="fld-modal-confirm" onClick={() => onSelect('sh')}>Sac Bunt</button>
            </>
          )}
          {onBack && <button className="fld-modal-cancel" onClick={onBack}>← Back</button>}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// HIT LOCATION PROMPT
// ─────────────────────────────────────────────

function HitLocationPrompt({ onSelect, onBack }) {
  const infield = [
    { key: 'P', label: 'P', sub: 'Pitcher' },
    { key: '1B', label: '1B', sub: 'First' },
    { key: '2B', label: '2B', sub: 'Second' },
    { key: '3B', label: '3B', sub: 'Third' },
    { key: 'SS', label: 'SS', sub: 'Shortstop' },
    { key: 'C', label: 'C', sub: 'Catcher' },
  ];
  const outfield = [
    { key: 'LF', label: 'LF', sub: 'Left Field' },
    { key: 'LC Gap', label: 'LC Gap', sub: 'Left-Center' },
    { key: 'CF', label: 'CF', sub: 'Center Field' },
    { key: 'RC Gap', label: 'RC Gap', sub: 'Right-Center' },
    { key: 'RF', label: 'RF', sub: 'Right Field' },
  ];
  const btnStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 2,
    padding: '8px 4px',
    fontSize: 13,
    fontWeight: 700,
  };
  return (
    <div className="fld-modal-overlay">
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Hit Location</div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Infield</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {infield.map(({ key, label, sub }) => (
            <button
              key={key}
              className="fld-modal-confirm"
              style={btnStyle}
              onClick={() => onSelect(key)}
            >
              <span>{label}</span>
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>{sub}</span>
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Outfield</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {outfield.map(({ key, label, sub }) => (
            <button
              key={key}
              className="fld-modal-confirm"
              style={btnStyle}
              onClick={() => onSelect(key)}
            >
              <span>{label}</span>
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8 }}>{sub}</span>
            </button>
          ))}
        </div>
        {onBack && <button className="fld-modal-cancel" style={{ width: '100%' }} onClick={onBack}>← Back</button>}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// K TYPE PROMPT
// ─────────────────────────────────────────────

function KTypePrompt({ onSelect, onCancel }) {
  return (
    <div className="fld-modal-overlay" onClick={onCancel}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Strikeout Type</div>
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={() => onSelect('swinging')}>Swinging</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('looking')}>Looking</button>
          <button className="fld-modal-cancel" onClick={onCancel}>← Back</button>
        </div>
      </div>
    </div>
  );
}

function D3KTypePrompt({ onSelect, onCancel }) {
  return (
    <div className="fld-modal-overlay" onClick={onCancel}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Dropped 3rd Strike</div>
        <div className="fld-modal-body">How did the batter swing?</div>
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={() => onSelect('swinging')}>Swinging</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('looking')}>Looking</button>
          <button className="fld-modal-cancel" onClick={onCancel}>← Back</button>
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────
// STRIKE DETAIL MODAL
// Shown when +S tapped and strikeDetail pref is ON
// ─────────────────────────────────────────────

function StrikeDetailModal({ twoStrikes, onSelect, onCancel }) {
  return (
    <div className="fld-modal-overlay" onClick={onCancel}>
      <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="fld-modal-title">Strike Type</div>
        <div className="fld-modal-actions">
          <button className="fld-modal-confirm" onClick={() => onSelect('swinging')}>Swinging</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('looking')}>Looking</button>
          <button className="fld-modal-confirm" onClick={() => onSelect('foul')}>
            Foul {twoStrikes && <span style={{ fontSize: 11, opacity: 0.7 }}>(2 strikes — count holds)</span>}
          </button>
          {twoStrikes && (
            <>
              <button className="fld-modal-confirm" onClick={() => onSelect('d3k')}>Dropped 3rd Strike</button>
            </>
          )}
          <button className="fld-modal-cancel" onClick={onCancel}>← Back</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// BATTING STATS DISPLAY
// ─────────────────────────────────────────────

function BattingStats({ stats, ratios }) {
  const fmt = (n, dec = 3) => n.toFixed(dec).replace(/^0/, '');

  return (
    <div className="bat-stats-panel">
      <div className="bat-rate-row">
        {[
          { label: 'AVG', value: fmt(ratios.avg) },
          { label: 'OBP', value: fmt(ratios.obp) },
          { label: 'SLG', value: fmt(ratios.slg) },
          { label: 'OPS', value: ratios.ops.toFixed(3).replace(/^0/, '') },
        ].map(({ label, value }) => (
          <div key={label} className="bat-rate-cell">
            <div className="bat-rate-value">{stats.ab > 0 ? value : '—'}</div>
            <div className="bat-rate-label">{label}</div>
          </div>
        ))}
      </div>

      <div className="bat-count-row">
        {[
          { label: 'PA', value: stats.pa },
          { label: 'AB', value: stats.ab },
          { label: 'H', value: stats.h },
          { label: 'R', value: stats.runs },
          { label: 'RBI', value: stats.rbi },
          { label: 'BB', value: stats.bb },
          { label: 'K', value: stats.k },
          { label: 'SB', value: stats.sb },
        ].map(({ label, value }) => (
          <div key={label} className="bat-count-cell">
            <div className="bat-count-value">{value}</div>
            <div className="bat-count-label">{label}</div>
          </div>
        ))}
      </div>

      {stats.ab_risp > 0 && (
        <div className="bat-risp-row">
          <span className="bat-risp-label">RISP</span>
          <span className="bat-risp-value">
            {stats.h_risp}/{stats.ab_risp} ·{' '}
            {fmt(stats.ab_risp > 0 ? stats.h_risp / stats.ab_risp : 0)}
          </span>
          {stats.rbi_risp > 0 && (
            <span className="bat-risp-rbi">{stats.rbi_risp} RBI w/RISP</span>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN BATTING TAB
// ─────────────────────────────────────────────

function BatEditModal({ atBatId, paAction, countActions, onSave, onCancel }) {
  const [outcome, setOutcome] = useState(paAction.outcome);
  const [hitType, setHitType] = useState(paAction.hitType || '');
  const [hitLocation, setHitLocation] = useState(paAction.hitLocation || '');
  const [strikeoutType, setStrikeoutType] = useState(paAction.strikeoutType || '');
  const [rbi, setRbi] = useState(paAction.rbi || 0);

  // Derive initial counts directly from countActions
  const initCounts = countActions.reduce((acc, a) => {
    if (a.outcome === 'count_ball') acc.balls++;
    else if (a.outcome === 'count_strike') {
      const pc = a.snapshot?.pitchCounts || {};
      if (pc.fouls > (acc._lastFouls || 0)) { acc.fouls++; acc._lastFouls = pc.fouls; }
      else if (pc.looking > (acc._lastLooking || 0)) { acc.looking++; acc._lastLooking = pc.looking; }
      else if (pc.swinging > (acc._lastSwinging || 0)) { acc.swinging++; acc._lastSwinging = pc.swinging; }
    }
    return acc;
  }, { balls: 0, swinging: 0, looking: 0, fouls: 0, _lastFouls: 0, _lastLooking: 0, _lastSwinging: 0 });

  const [balls, setBalls] = useState(initCounts.balls);
  const [swinging, setSwinging] = useState(initCounts.swinging);
  const [looking, setLooking] = useState(initCounts.looking);
  const [fouls, setFouls] = useState(initCounts.fouls);

  const isHit = ['single', 'double', 'triple', 'hr', 'iphr'].includes(outcome);
  const isK = outcome === 'k';
  const isD3K = outcome === 'd3k_reached' || outcome === 'd3k_out';
  const showRBI = true;

  const btnStyle = (active) => ({
    padding: '8px 12px',
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 8,
    border: active ? 'none' : '1.5px solid var(--border)',
    background: active ? '#1a1e2e' : 'var(--surface)',
    color: active ? '#fff' : 'var(--text-2)',
    cursor: 'pointer',
    flex: '0 0 auto',
  });

  const CountField = ({ label, value, onChange }) => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button style={{ ...btnStyle(false), padding: '4px 12px', fontSize: 16 }} onClick={() => onChange(Math.max(0, value - 1))}>−</button>
        <span style={{ fontSize: 20, fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{value}</span>
        <button style={{ ...btnStyle(false), padding: '4px 12px', fontSize: 16 }} onClick={() => onChange(value + 1)}>+</button>
      </div>
    </div>
  );

  return (
    <div className="fld-modal-overlay" onClick={onCancel}>
      <div className="fld-modal-card" style={{ maxHeight: '85vh', overflowY: 'auto', maxWidth: 420, width: '100%' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="fld-modal-title" style={{ margin: 0 }}>Edit At Bat</div>
          <button className="fld-modal-cancel" style={{ border: 'none', padding: '4px 8px' }} onClick={onCancel}>✕</button>
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Pitch Count</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20, padding: '12px', background: 'var(--surface-2)', borderRadius: 10 }}>
          <CountField label="Balls" value={balls} onChange={setBalls} />
          <CountField label="Swinging" value={swinging} onChange={setSwinging} />
          <CountField label="Looking" value={looking} onChange={setLooking} />
          <CountField label="Fouls" value={fouls} onChange={setFouls} />
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Result</div>
        <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Hits</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {[['single','Single'],['double','Double'],['triple','Triple'],['hr','Home Run'],['iphr','Inside Park HR']].map(([o,l]) => (
            <button key={o} style={btnStyle(outcome === o)} onClick={() => setOutcome(o)}>{l}</button>
          ))}
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Outs</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          {[['k','Strikeout'],['out','Out'],['gidp','Double Play'],['sf','Sac Fly'],['sh','Sac Bunt'],['fc',"Fielder's Choice"],['d3k_out','Dropped 3rd Strike — Out']].map(([o,l]) => (
            <button key={o} style={btnStyle(outcome === o)} onClick={() => setOutcome(o)}>{l}</button>
          ))}
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>On Base</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
          {[['bb','Walk'],['ibb','Int. Walk'],['hbp','Hit by Pitch'],['roe','Reached on Error'],['ci',"Catcher's Interference"],['d3k_reached','Dropped 3rd Strike — Safe']].map(([o,l]) => (
            <button key={o} style={btnStyle(outcome === o)} onClick={() => setOutcome(o)}>{l}</button>
          ))}
        </div>

        {isHit && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Hit Type</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {['Line Drive','Ground Ball','Fly Ball','Pop Up','Bunt'].map(t => (
                <button key={t} style={btnStyle(hitType === t)} onClick={() => setHitType(hitType === t ? '' : t)}>{t}</button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Hit Location</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Infield</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {[['P','Pitcher'],['1B','First'],['2B','Second'],['3B','Third'],['SS','Shortstop'],['C','Catcher']].map(([key, label]) => (
                <button key={key} style={btnStyle(hitLocation === key)} onClick={() => setHitLocation(hitLocation === key ? '' : key)}>
                  <span style={{ display: 'block', fontWeight: 700 }}>{key}</span>
                  <span style={{ display: 'block', fontSize: 10, fontWeight: 400, opacity: 0.8 }}>{label}</span>
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 4 }}>Outfield</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
              {[['LF','Left Field'],['LC Gap','Left-Center'],['CF','Center Field'],['RC Gap','Right-Center'],['RF','Right Field']].map(([key, label]) => (
                <button key={key} style={btnStyle(hitLocation === key)} onClick={() => setHitLocation(hitLocation === key ? '' : key)}>
                  <span style={{ display: 'block', fontWeight: 700 }}>{key}</span>
                  <span style={{ display: 'block', fontSize: 10, fontWeight: 400, opacity: 0.8 }}>{label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {isK && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Strikeout Type</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {[['swinging','Swinging'],['looking','Looking']].map(([t,l]) => (
                <button key={t} style={{ ...btnStyle(strikeoutType === t), flex: 1 }} onClick={() => setStrikeoutType(strikeoutType === t ? '' : t)}>{l}</button>
              ))}
            </div>
          </>
        )}

        {showRBI && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>RBI</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
              <button style={{ ...btnStyle(false), padding: '6px 20px', fontSize: 20 }} onClick={() => setRbi(Math.max(0, rbi - 1))}>−</button>
              <span style={{ fontSize: 24, fontWeight: 700, minWidth: 32, textAlign: 'center' }}>{rbi}</span>
              <button style={{ ...btnStyle(false), padding: '6px 20px', fontSize: 20 }} onClick={() => setRbi(Math.min(4, rbi + 1))}>+</button>
            </div>
          </>
        )}

        <button
          className="fld-modal-confirm"
          style={{ width: '100%', marginTop: 8 }}
          onClick={() => onSave({
            outcome,
            hitType: isHit ? hitType : null,
            hitLocation: isHit ? hitLocation : null,
            strikeoutType: (isK || isD3K) ? strikeoutType : null,
            rbi: showRBI ? rbi : 0,
            pitchCounts: { balls, swinging, looking, fouls },
          })}
        >
          Save Changes
        </button>
        <button className="fld-modal-cancel" style={{ width: '100%', marginTop: 8 }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function AtBatLog({ actions, currentAtBatId, onClose, onEditAtBat, onDeleteAtBat }) {
  const [editingAtBatId, setEditingAtBatId] = useState(null);

  // Group actions by atBatId, preserving order of first appearance
  const PA_ENDING = ['k','bb','ibb','hbp','ci','gidp','d3k_reached','d3k_out','single','double','triple','hr','iphr','out','roe','fc','sh','sf'];
  const BASE_ACTIONS = ['sb','cs','pickedOff','run'];
  const COUNT_ACTIONS = ['count_ball','count_strike'];

  const groups = [];
  const seenIds = new Set();
  for (const a of actions) {
    const groupId = a.atBatId ? String(a.atBatId) : ('orphan_' + String(a.id));
    if (!seenIds.has(groupId)) {
      seenIds.add(groupId);
      groups.push(groupId);
    }
  }

  const groupedActions = groups.map(groupId => {
    const groupActions = actions.filter(a =>
      groupId.startsWith('orphan_')
        ? (!a.atBatId && String(a.id) === groupId.replace('orphan_', ''))
        : String(a.atBatId) === groupId
    );
    const paAction = groupActions.find(a => PA_ENDING.includes(a.outcome));
    const countActions = groupActions.filter(a => COUNT_ACTIONS.includes(a.outcome));
    const baseActions = groupActions.filter(a => BASE_ACTIONS.includes(a.outcome));
    return { groupId, groupActions, paAction, countActions, baseActions };
  });

  // Find the group being edited
  const editingGroup = editingAtBatId ? groupedActions.find(g => g.groupId === editingAtBatId) : null;

  if (editingGroup && editingGroup.paAction) {
    return (
      <BatEditModal
        atBatId={editingGroup.groupId}
        paAction={editingGroup.paAction}
        countActions={editingGroup.countActions}
        onSave={(changes) => {
          onEditAtBat(editingGroup.groupId, editingGroup.paAction, changes, editingGroup.countActions);
          setEditingAtBatId(null);
        }}
        onCancel={() => setEditingAtBatId(null)}
      />
    );
  }

  // AB number = count of completed (non-current) groups with a PA-ending action, in order
  let abCounter = 0;

  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" style={{ maxHeight: '80vh', overflowY: 'auto', maxWidth: 420, width: '100%' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="fld-modal-title" style={{ margin: 0 }}>At Bat Log</div>
          <button className="fld-modal-cancel" style={{ border: 'none', padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        {groupedActions.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '20px 0' }}>No at bats recorded yet.</div>
        )}
        {currentAtBatId && !groupedActions.find(g => g.groupId === currentAtBatId) && (
          <div style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--crimson)' }}>Current At Bat</span>
          </div>
        )}
        {groupedActions.filter(({ groupId, paAction }) => {
          const isCurrent = groupId === currentAtBatId;
          return isCurrent || paAction;
        }).map(({ groupId, paAction, countActions, baseActions }) => {
          const isCurrent = groupId === currentAtBatId;
          const balls = countActions.filter(a => a.outcome === 'count_ball').length;
          const strikes = countActions.filter(a => a.outcome === 'count_strike').length;
          const hasCounts = balls > 0 || strikes > 0;

          if (!isCurrent && paAction) abCounter++;
          const abNum = abCounter;

          const resultLabel = isCurrent
            ? 'Current At Bat'
            : paAction
              ? formatOutcome(paAction.outcome, paAction.strikeoutType)
              : '—';

          const subDetails = [];
          if (!isCurrent && paAction) {
            if (hasCounts) subDetails.push(`${balls}B ${strikes}K`);
            if (paAction.hitType) subDetails.push(paAction.hitType);
            if (paAction.rbi > 0) subDetails.push(`${paAction.rbi} RBI`);
            if (paAction.risp) subDetails.push('RISP');
            baseActions.forEach(a => subDetails.push(formatOutcome(a.outcome)));
          }

          return (
            <div key={groupId} style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <span style={{ fontSize: 11, color: 'var(--text-3)', marginRight: 8, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    {isCurrent ? '' : `AB ${abNum}`}
                  </span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: isCurrent ? 'var(--crimson)' : 'var(--text)' }}>
                    {resultLabel}
                  </span>
                </div>
                {!isCurrent && paAction && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: 'var(--text-2)', padding: '2px 6px' }}
                      onClick={() => setEditingAtBatId(groupId)}
                    >✏️</button>
                    <button
                      style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, color: '#c0001a', padding: '2px 6px' }}
                      onClick={() => {
                        if (window.confirm('Delete this at-bat and all its actions?')) onDeleteAtBat(groupId);
                      }}
                    >🗑️</button>
                  </div>
                )}
              </div>
              {subDetails.length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, paddingLeft: 2 }}>
                  {subDetails.join(' · ')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BatGearModal({ stats, ratios, onClose, prefs, onTogglePref }) {
  return (
    <div className="fld-modal-overlay" onClick={onClose}>
      <div className="fld-modal-card" style={{ maxHeight: '80vh', overflowY: 'auto', maxWidth: 420, width: '100%' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div className="fld-modal-title" style={{ margin: 0 }}>Game Stats</div>
          <button className="fld-modal-cancel" style={{ border: 'none', padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        {stats.pa === 0 ? (
          <div style={{ textAlign: 'center', color: 'var(--text-3)', padding: '20px 0' }}>No stats yet.</div>
        ) : (
          <BattingStats stats={stats} ratios={ratios} />
        )}
        <div style={{ marginTop: 20, borderTop: '1px solid var(--border-light)', paddingTop: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 12 }}>Tracking Options</div>
          {[
            { key: 'count', label: 'Ball/Strike Count' },
            { key: 'strikeDetail', label: 'Strike Type (Swinging/Looking/Foul)' },
            { key: 'hitType', label: 'Hit Type' },
            { key: 'hitLocation', label: 'Hit Location' },
            { key: 'strikeoutType', label: 'Strikeout Type' },
          ].map(({ key, label }) => (
            <div
              key={key}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-light)' }}
            >
              <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{label}</span>
              <button
                onClick={() => onTogglePref(key)}
                style={{
                  background: prefs[key] ? 'var(--amber)' : 'var(--surface-2)',
                  border: '1px solid var(--border)',
                  borderRadius: 20,
                  padding: '4px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  color: prefs[key] ? '#0d0f14' : 'var(--text-3)',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-ui)',
                  minWidth: 52,
                }}
              >
                {prefs[key] ? 'ON' : 'OFF'}
              </button>
            </div>
          ))}
        </div>
        <button className="fld-modal-cancel" style={{ marginTop: 16, width: '100%' }} onClick={onClose}>Close</button>
      </div>
    </div>
  );
}

export default function BattingTab({ game }) {
  const { state, battingRatios, undoLastCompletedAtBat, undoLastBattingAction, addCountAction, updateBattingAction, deleteBattingAction, deleteAtBat } = game;
  const { batting } = state;
  const stats = batting.stats;
  const prefs = batting.trackingPrefs;
  const currentAB = batting.currentAtBat;

  // At-bat flow
  const [step, setStep] = useState('idle');
  const [pending, setPending] = useState({});
  const [readyForAB, setReadyForAB] = useState(false);

  // Per-at-bat pitch tracking (count pref)
  const [pitchCounts, setPitchCounts] = useState({
    swinging: 0,
    looking: 0,
    fouls: 0,
    balls: 0,
  });
  const [showStrikeModal, setShowStrikeModal] = useState(false);

  function resetPitchCounts() {
    setPitchCounts({ swinging: 0, looking: 0, fouls: 0, balls: 0 });
    setShowStrikeModal(false);
  }

  function handleStrikeModalSelect(type) {
    const twoStrikes = currentAB.strikeCounter >= 2;
    if (type === 'foul') {
      if (!twoStrikes) {
        const newStrikes = currentAB.strikeCounter + 1;
        const newCounts = { ...pitchCounts, fouls: pitchCounts.fouls + 1 };
        setPitchCounts(newCounts);
        addCountAction('count_strike', { strikeCounter: newStrikes, pitchCounts: newCounts });
      } else {
        // Foul on 2 strikes — count doesn't advance but log it
        const newCounts = { ...pitchCounts, fouls: pitchCounts.fouls + 1 };
        setPitchCounts(newCounts);
        addCountAction('count_strike', { strikeCounter: 2, pitchCounts: newCounts });
      }
    } else if (type === 'swinging') {
      const newCounts = { ...pitchCounts, swinging: pitchCounts.swinging + 1 };
      setPitchCounts(newCounts);
      if (twoStrikes) {
        addCountAction('count_strike', { strikeCounter: 3, pitchCounts: newCounts });
        setShowStrikeModal(false);
        const liveRisp = !!(liveBaseState.second || liveBaseState.third);
        commitAction({ outcome: 'k', strikeoutType: 'swinging', risp: liveRisp, baseState: liveBaseState }, newCounts);
        return;
      }
      const newStrikes = Math.min(2, currentAB.strikeCounter + 1);
      addCountAction('count_strike', { strikeCounter: newStrikes, pitchCounts: newCounts });
    } else if (type === 'looking') {
      const newCounts = { ...pitchCounts, looking: pitchCounts.looking + 1 };
      setPitchCounts(newCounts);
      if (twoStrikes) {
        addCountAction('count_strike', { strikeCounter: 3, pitchCounts: newCounts });
        setShowStrikeModal(false);
        const liveRisp = !!(liveBaseState.second || liveBaseState.third);
        commitAction({ outcome: 'k', strikeoutType: 'looking', risp: liveRisp, baseState: liveBaseState }, newCounts);
        return;
      }
      const newStrikes = Math.min(2, currentAB.strikeCounter + 1);
      addCountAction('count_strike', { strikeCounter: newStrikes, pitchCounts: newCounts });
    } else if (type === 'd3k') {
      setShowStrikeModal(false);
      setPending((prev) => ({ ...prev, outcome: 'd3k' }));
      setStep('d3ktype');
      return;
    }
    setShowStrikeModal(false);
  }

  // My player's live base position
  const [playerPosition, setPlayerPosition] = useState(batting.playerPosition ?? null);
  const [baserunnerHistory, setBaserunnerHistory] = useState([]);
  // Advancement prompt target base
  const [advancementTarget, setAdvancementTarget] = useState(null);
  // Score prompt
  const [showScorePrompt, setShowScorePrompt] = useState(false);
  // Out prompt (tapping current base)
  const [showOutPrompt, setShowOutPrompt] = useState(false);
  const [pendingPickoffModal, setPendingPickoffModal] = useState(null);
  const [liveBaseState, setLiveBaseState] = useState({ first: false, second: false, third: false });
  const [diamondVisible, setDiamondVisible] = useState(true);
  const [baserunnerModalBase, setBaserunnerModalBase] = useState(null);
  const [showBatGear, setShowBatGear] = useState(false);
  const [showAtBatLog, setShowAtBatLog] = useState(false);
  const [showUndoBlocked, setShowUndoBlocked] = useState(false);

  // Restore mid-at-bat state on mount (e.g. after navigating away and back)
  React.useEffect(() => {
    if (batting.atBatPhase === 'active') {
      const lastAction = batting.actions[batting.actions.length - 1];
      const snap = lastAction?.snapshot;
      if (snap) {
        if (snap.pitchCounts) setPitchCounts(snap.pitchCounts);
        if (snap.playerPosition !== undefined) setPlayerPosition(snap.playerPosition ?? null);
      }
      const restoredBaseState = batting.currentAtBat?.baseState;
      if (restoredBaseState) {
        setLiveBaseState(restoredBaseState);
        setDiamondVisible(runnersOnCount(restoredBaseState) > 0);
      }
      setReadyForAB(false);
      setStep('outcome');
    }
  }, []);

  // ── Player position helpers ──────────────

  const posLabel = {
    first: '1st Base',
    second: '2nd Base',
    third: '3rd Base',
  };

  function autoPlacePlayer(outcome) {
    const toFirst = [
      'single',
      'bb',
      'ibb',
      'hbp',
      'roe',
      'd3k_reached',
      'ci',
      'fc',
    ];
    const toSecond = ['double'];
    const toThird = ['triple'];
    const scores = ['hr', 'iphr'];
    const outs = ['out', 'k', 'gidp', 'sf', 'sh', 'd3k_out'];

    if (toFirst.includes(outcome)) {
      setPlayerPosition('first');
    } else if (toSecond.includes(outcome)) {
      setPlayerPosition('second');
    } else if (toThird.includes(outcome)) {
      setPlayerPosition('third');
    } else if (scores.includes(outcome)) {
      // Auto-record the run
      game.addBattingAction({ outcome: 'run', playerPosition });
      setPlayerPosition(null);
    } else if (outs.includes(outcome)) {
      setPlayerPosition(null);
    }
  }

  function pushBaserunnerHistory(prevPosition, actionsToUndo = 0) {
    setBaserunnerHistory(h => [...h, { prevPosition, actionsToUndo }]);
  }
  function handleAdvanceTo(base) {
    if (base === playerPosition) {
      // Tapping current base = out prompt
      setShowOutPrompt(true);
    } else if (base === 'home') {
      setShowScorePrompt(true);
    } else {
      setAdvancementTarget(base);
    }
  }

  function handleAdvancementConfirm(how) {
    if (how === 'attempted_steal') {
      setPendingPickoffModal({ type: 'cs', base: advancementTarget });
      setAdvancementTarget(null);
      return;
    }
    const outOutcomes = ['fc_out', 'thrown_out', 'failed_tag_up'];
    if (outOutcomes.includes(how)) {
      pushBaserunnerHistory(playerPosition, 1);
      game.addBattingAction({ outcome: how, playerPosition });
      setPlayerPosition(null);
      setAdvancementTarget(null);
      setStep('idle');
      setReadyForAB(false);
      if (batting.atBatPhase === 'active') game.resetAtBat();
      return;
    }
    // Safe advancement — no reducer action
    pushBaserunnerHistory(playerPosition, 0);
    setPlayerPosition(advancementTarget);
    setAdvancementTarget(null);
    setStep('idle');
    setReadyForAB(false);
    if (batting.atBatPhase === 'active') game.resetAtBat();
  }

  function handleOutConfirm(how) {
    if (how === 'picked_off') {
      setShowOutPrompt(false);
      setPendingPickoffModal({ type: 'pickoff', base: playerPosition });
      return;
    } else if (how === 'caught_stealing') {
      setShowOutPrompt(false);
      setPendingPickoffModal({ type: 'cs', base: playerPosition });
      return;
    }
    // failed_tag_up just clears position — no separate stat
    pushBaserunnerHistory(playerPosition, 0);
    setPlayerPosition(null);
    setShowOutPrompt(false);
    setStep('idle');
    setReadyForAB(false);
    if (batting.atBatPhase === 'active') game.resetAtBat();
  }

  function handleScore(how) {
    const actionsToUndo = how === 'stolen_base' ? 2 : 1;
    pushBaserunnerHistory(playerPosition, actionsToUndo);
    if (how === 'stolen_base') {
      game.addBattingAction({ outcome: 'sb', playerPosition });
    }
    game.addBattingAction({ outcome: 'run', playerPosition });
    setPlayerPosition(null);
    setShowScorePrompt(false);
    setReadyForAB(false);
    setStep('idle');
    setPending({});
    if (batting.atBatPhase === 'active') game.resetAtBat();
  }

  function handleOutAtHome() {
    pushBaserunnerHistory(playerPosition, 0);
    setPlayerPosition(null);
    setShowScorePrompt(false);
    setStep('idle');
    setPending({});
    if (batting.atBatPhase === 'active') game.resetAtBat();
  }

  function handleInningEnded() {
    pushBaserunnerHistory(playerPosition, 0);
    setPlayerPosition(null);
  }

  // ── At-bat flow ──────────────────────────

  function handleStartAtBat(baseState, outCount = 0) {
    game.startAtBat(baseState, prefs);
    setLiveBaseState({ ...baseState });
    setPending(prev => ({ ...prev, outCount }));
    setStep('outcome');
    setBaserunnerHistory([]);
    setDiamondVisible(runnersOnCount(baseState) > 0);
  }

  function applyLiveBaseStateChange(updater) {
    setLiveBaseState((prev) => {
      const updated = updater(prev);
      game.updateAtBatCount(undefined, undefined, updated);
      return updated;
    });
  }

  function handleAdvanceRunner(base) {
    const nextBase = base === 'first' ? 'second' : base === 'second' ? 'third' : null;
    applyLiveBaseStateChange((prev) => {
      const updated = { ...prev, [base]: false };
      if (nextBase) updated[nextBase] = true;
      return updated;
    });
    setBaserunnerModalBase(null);
  }

  function handleRemoveRunner(base) {
    applyLiveBaseStateChange((prev) => ({ ...prev, [base]: false }));
    setBaserunnerModalBase(null);
  }

  function handleOutcome(outcome) {
    const liveRisp = !!(liveBaseState.second || liveBaseState.third);
    const payload = {
      outcome,
      risp: liveRisp,
      baseState: liveBaseState,
      outCount: pending.outCount ?? 0,
    };
    setPending(payload);
    // K flow — go to ktype or straight to kresult
    if (outcome === 'k') {
      if (prefs.strikeoutType) {
        setStep('ktype');
      } else {
        setStep('kresult');
      }
      return;
    }
    // Ball in play — always go to hit type
    if (outcome === 'ballInPlay') {
      setStep('hittype');
      return;
    }
    // Everything else (walk, hbp, ibb, roe, fc, ci) — commit immediately
    commitAction(payload);
  }

  function handleD3KType(strikeoutType) {
    const newCounts = strikeoutType === 'swinging'
      ? { ...pitchCounts, swinging: pitchCounts.swinging + 1 }
      : { ...pitchCounts, looking: pitchCounts.looking + 1 };
    setPitchCounts(newCounts);
    addCountAction('count_strike', { strikeCounter: 3, pitchCounts: newCounts });
    setPending((prev) => ({ ...prev, strikeoutType }));
    setStep('kresult');
  }
  function handleKType(strikeoutType) {
    const updated = { ...pending, strikeoutType };
    setPending(updated);
    setStep('kresult');
  }

  function handleKResult(kOutcome) {
    const updated = { ...pending, outcome: kOutcome };
    commitAction(updated);
  }

  function handleHitType(hitType) {
    const updated = { ...pending, hitType };
    setPending(updated);
    setStep('result');
  }

  function handleResult(outcome) {
    const updated = { ...pending, outcome };
    setPending(updated);
    if (outcome === 'out') {
      setStep('outsubtype');
      return;
    }
    // Hit outcomes
    if (prefs.hitLocation) {
      setStep('hitlocation');
      return;
    }
    if (
      runnersOnCount(liveBaseState) > 0 ||
      outcome === 'hr' ||
      outcome === 'iphr'
    ) {
      setStep('rbi');
      return;
    }
    commitAction(updated);
  }

  function handleOutSubtype(outcome) {
    // Map 'caught' to 'out' for storage — it's just a plain out
    const mappedOutcome = outcome === 'caught' ? 'out' : outcome;
    const updated = { ...pending, outcome: mappedOutcome };
    setPending(updated);
    const outCount = updated.outCount ?? 0;
    const hitType = updated.hitType;
    const isAirBall = ['Fly Ball', 'Pop Up', 'Line Drive'].includes(hitType);

    // Air ball caught out — never an RBI
    if (isAirBall && mappedOutcome === 'out') {
      if (prefs.hitLocation) { setStep('hitlocation'); return; }
      commitAction(updated);
      return;
    }
    // Sac fly — always ask RBI (already can't happen with 2 outs)
    if (mappedOutcome === 'sf') {
      if (prefs.hitLocation) { setStep('hitlocation'); return; }
      setStep('rbi');
      return;
    }
    // GIDP with 1 out — 3rd out is force, no RBI
    if (mappedOutcome === 'gidp' && outCount === 1) {
      if (prefs.hitLocation) { setStep('hitlocation'); return; }
      commitAction(updated);
      return;
    }
    // Out at 1st with 2 outs — force out ends inning, no RBI
    if (mappedOutcome === 'out' && outCount === 2) {
      if (prefs.hitLocation) { setStep('hitlocation'); return; }
      commitAction(updated);
      return;
    }
    // Everything else — ask RBI if runners on base
    if (prefs.hitLocation) { setStep('hitlocation'); return; }
    if (runnersOnCount(updated.baseState) > 0) {
      setStep('rbi');
      return;
    }
    commitAction(updated);
  }

  function handleHitLocation(hitLocation) {
    const updated = { ...pending, hitLocation };
    setPending(updated);

    const { outcome, baseState } = updated;

    // Outs — apply RBI rules per matrix
    if (['out', 'gidp', 'sh', 'sf'].includes(outcome)) {
      const outCount = updated.outCount ?? 0;
      const hitType = updated.hitType;
      const isAirBall = ['Fly Ball', 'Pop Up', 'Line Drive'].includes(hitType);
      // Air ball caught out — no RBI
      if (isAirBall && outcome === 'out') {
        commitAction(updated);
        return;
      }
      // Sac fly — always RBI
      if (outcome === 'sf') {
        setStep('rbi');
        return;
      }
      // GIDP with 1 out — force ends inning, no RBI
      if (outcome === 'gidp' && outCount === 1) {
        commitAction(updated);
        return;
      }
      // Out at 1st with 2 outs — force ends inning, no RBI
      if (outcome === 'out' && outCount === 2) {
        commitAction(updated);
        return;
      }
      // Everything else — ask RBI if runners on base
      if (runnersOnCount(baseState) > 0) {
        setStep('rbi');
        return;
      }
      commitAction(updated);
      return;
    }

    // SF needs RBI after location
    if (outcome === 'sf') {
      setStep('rbi');
      return;
    }

    // Hits — check for RBI
    if (
      runnersOnCount(baseState) > 0 ||
      outcome === 'hr' ||
      outcome === 'iphr'
    ) {
      setStep('rbi');
      return;
    }
    commitAction(updated);
  }

  function handleRBI(rbi) {
    const updated = { ...pending, rbi };
    commitAction(updated);
  }

  function commitAction(payload, overridePitchCounts) {
    const finalPayload = {
      ...payload,
      pitchCounts: overridePitchCounts ? { ...overridePitchCounts } : { ...pitchCounts },
      playerPosition,
    };
    game.addBattingAction(finalPayload);
    autoPlacePlayer(payload.outcome);
    setStep('idle');
    setPending({});
    setReadyForAB(false);
    resetPitchCounts();
  }

  function goBack() {
    setStep(batting.atBatPhase === 'active' ? 'outcome' : 'idle');
    setPending({});
  }

  function cancelAtBat() {
    game.resetAtBat();
    setStep('idle');
    setPending({});
    resetPitchCounts();
  }

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────

  return (
    <div className="bat-tab">
      {/* ── Status badge ─────────────────── */}
      <div className="bat-top-bar">
        <StatusToggle game={game} />
        {batting.atBatPhase === 'active' && (
          <div className={`bat-risp-badge ${(liveBaseState.second || liveBaseState.third) ? 'on' : 'off'}`}>
            {(liveBaseState.second || liveBaseState.third) ? '🔥 RISP' : 'No RISP'}
          </div>
        )}
        <button
          className="pt-gear-btn"
          onClick={() => setShowBatGear(true)}
          style={{ marginLeft: 'auto' }}
        >
          ⚙️
        </button>
      </div>
      {showBatGear && (
        <BatGearModal
          stats={stats}
          ratios={battingRatios}
          onClose={() => setShowBatGear(false)}
          prefs={prefs}
          onTogglePref={(key) => game.setBattingTrackingPrefs({ [key]: !prefs[key] })}
        />
      )}
      {showAtBatLog && (
        <AtBatLog
          actions={batting.actions}
          currentAtBatId={batting.currentAtBatId}
          onClose={() => setShowAtBatLog(false)}
          onEditAtBat={(atBatId, paAction, changes, oldCountActions) => {
            // Update the PA-ending action
            updateBattingAction(paAction.id, {
              outcome: changes.outcome,
              hitType: changes.hitType,
              hitLocation: changes.hitLocation,
              strikeoutType: changes.strikeoutType,
              rbi: changes.rbi,
            });
            // Delete old count actions for this at-bat
            oldCountActions.forEach(a => deleteBattingAction(a.id));
            // Add new count actions: balls first, then strikes by type
            const newCounts = changes.pitchCounts;
            for (let i = 0; i < newCounts.balls; i++) {
              game.addCountAction('count_ball', {
                ballCounter: i + 1,
                pitchCounts: { balls: i + 1, swinging: 0, looking: 0, fouls: 0 },
              });
            }
            for (let i = 0; i < newCounts.swinging; i++) {
              game.addCountAction('count_strike', {
                strikeCounter: i + 1,
                pitchCounts: { balls: newCounts.balls, swinging: i + 1, looking: 0, fouls: 0 },
              });
            }
            for (let i = 0; i < newCounts.looking; i++) {
              game.addCountAction('count_strike', {
                strikeCounter: i + 1,
                pitchCounts: { balls: newCounts.balls, swinging: newCounts.swinging, looking: i + 1, fouls: 0 },
              });
            }
            for (let i = 0; i < newCounts.fouls; i++) {
              game.addCountAction('count_strike', {
                strikeCounter: Math.min(2, i + 1),
                pitchCounts: { balls: newCounts.balls, swinging: newCounts.swinging, looking: newCounts.looking, fouls: i + 1 },
              });
            }
            autoPlacePlayer(changes.outcome);
          }}
          onDeleteAtBat={(atBatId) => deleteAtBat(atBatId)}
        />
      )}

      {/* ── Live count display (during at-bat) ── */}
      {batting.atBatPhase === 'active' && (
        <>
          <BatCountDisplay
            balls={prefs.count ? currentAB.ballCounter : 0}
            strikes={prefs.count ? currentAB.strikeCounter : 0}
            pitchCount={pitchCounts.balls + pitchCounts.swinging + pitchCounts.looking + pitchCounts.fouls}
            countEnabled={prefs.count}
            onOpenSettings={() => setShowBatGear(true)}
            onBallTap={() => {
              if (!prefs.count) return;
              const newBalls = currentAB.ballCounter + 1;
              const newBallCounts = { ...pitchCounts, balls: pitchCounts.balls + 1 };
              setPitchCounts(newBallCounts);
              if (newBalls >= 4) {
                const liveRisp = !!(liveBaseState.second || liveBaseState.third);
                const basesLoaded = !!(liveBaseState.first && liveBaseState.second && liveBaseState.third);
                addCountAction('count_ball', { ballCounter: newBalls, pitchCounts: newBallCounts });
                if (basesLoaded) {
                  setPending({ outcome: 'bb', risp: liveRisp, baseState: liveBaseState, pitchCounts: newBallCounts });
                  setStep('rbi');
                  return;
                }
                commitAction({ outcome: 'bb', risp: liveRisp, baseState: liveBaseState }, newBallCounts);
                return;
              }
              addCountAction('count_ball', { ballCounter: newBalls, pitchCounts: newBallCounts });
            }}
            onStrikeTap={() => {
              if (!prefs.count) return;
              if (prefs.strikeDetail) {
                setShowStrikeModal(true);
              } else {
                const newStrikes = Math.min(2, currentAB.strikeCounter + 1);
                addCountAction('count_strike', { strikeCounter: newStrikes, pitchCounts });
              }
            }}
          />
        </>
      )}

      {/* Strike detail modal */}
      {showStrikeModal && (
        <StrikeDetailModal
          twoStrikes={currentAB.strikeCounter >= 2}
          onSelect={handleStrikeModalSelect}
          onCancel={() => setShowStrikeModal(false)}
        />
      )}

      {/* Pitch type summary removed — pitch count shown in count display */}
      {false && (
          <div className="bat-pitch-summary">
          </div>
        )}

      {/* ═══ OVERLAYS (advancement / score prompts) ═══ */}

      {advancementTarget && (
        <AdvancementPrompt
          from={playerPosition}
          to={advancementTarget}
          onConfirm={handleAdvancementConfirm}
          onCancel={() => setAdvancementTarget(null)}
        />
      )}

      {pendingPickoffModal && (
        <PickoffModal
          type={pendingPickoffModal.type}
          preselectedBase={pendingPickoffModal.base}
          skipPitcherQuestion={true}
          batterPerspective={true}
          onFieldingAction={(payload) => game.addFieldingAction(payload)}
          onConfirm={({ base, thrower, result }) => {
            if (pendingPickoffModal.type === 'pickoff') {
              pushBaserunnerHistory(playerPosition, 1);
              game.addBattingAction({ outcome: 'pickedOff', base, thrower, result, playerPosition });
            } else {
              pushBaserunnerHistory(playerPosition, 1);
              game.addBattingAction({ outcome: 'cs', base, thrower, result, playerPosition });
            }
            setPendingPickoffModal(null);
            if (result === 'safe') {
              if (pendingPickoffModal.type === 'cs' && pendingPickoffModal.base) {
                // Attempted steal safe — advance to the target base
                setPlayerPosition(pendingPickoffModal.base);
              }
              // Pickoff safe — runner stays, no position change needed
              return;
            }
            setPlayerPosition(null);
            setStep('idle');
            setReadyForAB(false);
            if (batting.atBatPhase === 'active') game.resetAtBat();
          }}
          onCancel={() => {
            setPendingPickoffModal(null);
          }}
        />
      )}

      {showOutPrompt && (
        <OutPrompt
          base={playerPosition}
          onConfirm={handleOutConfirm}
          onCancel={() => setShowOutPrompt(false)}
        />
      )}

      {showScorePrompt && (
        <ScorePrompt
          onScore={handleScore}
          onOut={handleOutAtHome}
          onCancel={() => setShowScorePrompt(false)}
        />
      )}

      {/* ═══ IDLE STATE ═══ */}

      {!advancementTarget &&
        !showScorePrompt &&
        step === 'idle' &&
        batting.atBatPhase === 'idle' && (
          <>
            {/* My player on base */}
            {playerPosition && (
              <div className="bat-player-position">
                <div className="bat-player-pos-header">
                  <span className="bat-player-pos-label">
                    Tracking: My Player — {posLabel[playerPosition]}
                  </span>
                  <button
                    className="bat-player-pos-clear"
                    onClick={() => setPlayerPosition(null)}
                    title="Remove from bases (out, picked off, etc.)"
                  >
                    ✕ Off Bases
                  </button>
                </div>
                <div className="bat-player-pos-diamond-wrap">
                  <PlayerPositionDiamond
                    position={playerPosition}
                    onAdvanceTo={handleAdvanceTo}
                  />
                </div>
                <div className="bat-player-pos-hint">
                  TRACK MY PLAYER — Tap next base to advance · Tap current base for pickoff attempt · Tap Home to score
                </div>
              </div>
            )}

            {/* Inning ended — only show when tracked player is on base */}
            {playerPosition && (
              <div className="bat-inning-ended-wrap">
                <button
                  className="bat-inning-ended-btn"
                  onClick={handleInningEnded}
                >
                  Inning Ended — Clear Bases
                </button>
              </div>
            )}

            {/* New at-bat setup */}
            {!playerPosition && (readyForAB ? (
              <AtBatSetup game={game} onStart={handleStartAtBat} />
            ) : (
              <button
                className="bat-new-ab-btn"
                onClick={() => { setReadyForAB(true); setBaserunnerHistory([]); }}
              >
                + New At Bat
              </button>
            ))}
          </>
        )}

      {/* ═══ AT-BAT FLOW STEPS ═══ */}

      {step === 'outcome' && (
        <div className="bat-outcome-area">
          {baserunnerModalBase && (
            <BaserunnerActionModal
              base={baserunnerModalBase}
              onAdvance={() => handleAdvanceRunner(baserunnerModalBase)}
              onRemove={() => handleRemoveRunner(baserunnerModalBase)}
              onClose={() => setBaserunnerModalBase(null)}
            />
          )}
          {(
            diamondVisible ? (
              <div className="bat-live-runners">
                <div className="bat-live-runners-label">RUNNERS ON BASE — TAP TO UPDATE</div>
                <BaseDiamond
                  baseState={liveBaseState}
                  onToggle={(key) => setLiveBaseState((prev) => {
                    const updated = { ...prev, [key]: !prev[key] };
                    game.updateAtBatCount(undefined, undefined, updated);
                    return updated;
                  })}
                  onOccupiedTap={(key) => setBaserunnerModalBase(key)}
                  interactive
                />
                <button
                  type="button"
                  onClick={() => setDiamondVisible(false)}
                  style={{ marginTop: 8, background: 'none', border: 'none', color: 'var(--crimson)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Hide diamond
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setDiamondVisible(true)}
                style={{ margin: '8px 0', background: 'none', border: '1px solid var(--crimson)', borderRadius: 6, padding: '6px 12px', color: 'var(--crimson)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Show diamond
              </button>
            )
          )}
          <div className="bat-outcome-section">
            <div className="bat-outcome-grid hits">
              <button
                className="bat-outcome-btn ball-in-play"
                onClick={() => handleOutcome('ballInPlay')}
              >
                <span className="bat-outcome-label">Ball in Play</span>
              </button>
              {!prefs.count && (
                <button
                  className="bat-outcome-btn out"
                  style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}
                  onClick={() => handleOutcome('k')}
                >
                  K
                </button>
              )}
              {!prefs.count && (
                <button
                  className="bat-outcome-btn reach"
                  style={{ flex: '1 1 calc(50% - 4px)', minWidth: 0 }}
                  onClick={() => handleOutcome('bb')}
                >
                  Walk
                </button>
              )}
            </div>
          </div>
          <div className="bat-outcome-section">
            <div className="bat-section-label">Reach</div>
            <div className="bat-outcome-grid reach">
              {[
                { outcome: 'hbp', label: 'Hit by Pitch' },
                { outcome: 'ibb', label: 'Int. Walk' },
                { outcome: 'ci', label: "Catcher's Interference" },
              ].map(({ outcome, label }) => (
                <button
                  key={outcome}
                  className="bat-outcome-btn reach"
                  onClick={() => handleOutcome(outcome)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <button className="bat-cancel-step" onClick={cancelAtBat}>
            Cancel At Bat
          </button>
        </div>
      )}

      {step === 'ktype' && <KTypePrompt onSelect={handleKType} onCancel={() => setStep('outcome')} />}
      {step === 'd3ktype' && (
        <D3KTypePrompt
          onSelect={handleD3KType}
          onCancel={() => setStep('outcome')}
        />
      )}
      {step === 'kresult' && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Dropped 3rd Strike</div>
            <div className="fld-modal-body">What happened on the play?</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={() => handleKResult('d3k_reached')}>Safe at 1st</button>
              <button className="fld-modal-confirm" onClick={() => handleKResult('d3k_out')}>Out at 1st</button>
              <button className="fld-modal-cancel" onClick={() => setStep('outcome')}>← Back</button>
            </div>
          </div>
        </div>
      )}
      {step === 'hittype' && (
        <HitTypePrompt
          onSelect={handleHitType}
          onBack={() => setStep('outcome')}
        />
      )}
      {step === 'result' && <ResultPrompt onSelect={handleResult} onBack={() => setStep('hittype')} />}
      {step === 'outsubtype' && <OutSubtypePrompt hitType={pending.hitType} runnersOn={runnersOnCount(liveBaseState)} onSelect={handleOutSubtype} onBack={() => setStep('result')} />}

      {step === 'hitlocation' && (
        <HitLocationPrompt
          onSelect={handleHitLocation}
          onBack={() => setStep('result')}
        />
      )}

      {step === 'rbi' && (
        <RBIPrompt
          outcome={pending.outcome}
          baseState={pending.baseState || currentAB.baseState}
          onConfirm={handleRBI}
          onBack={() => setStep(prefs.hitLocation ? 'hitlocation' : 'result')}
        />
      )}

      {/* Baserunning now handled via player position diamond */}

      {/* ── Stats summary ─────────────────── */}
      {(batting.actions.length > 0 || step === 'outcome') && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 16px 0' }}>
          <button className="pt-undo-last-btn" onClick={() => {
            const lastAction = batting.actions[batting.actions.length - 1];
            const canUndoAtBat = batting.currentAtBatId !== null &&
              lastAction?.atBatId === batting.currentAtBatId;
            const canUndoBaserunner = batting.currentAtBatId === null &&
              baserunnerHistory.length > 0;

            const canUndoCompletedAtBat = batting.currentAtBatId === null &&
              baserunnerHistory.length === 0 &&
              playerPosition !== null;

            if (!canUndoAtBat && !canUndoBaserunner && !canUndoCompletedAtBat) {
              setShowUndoBlocked(true);
              return;
            }

            if (canUndoCompletedAtBat) {
              const PA_ENDING_LOCAL = ['k','bb','ibb','hbp','ci','gidp','d3k_reached','d3k_out','single','double','triple','hr','iphr','out','roe','fc','sh','sf'];
              const lastPA = [...batting.actions].reverse().find(a => PA_ENDING_LOCAL.includes(a.outcome));
              const pitchCountsToRestore = lastPA?.snapshot?.pitchCounts || { swinging: 0, looking: 0, fouls: 0, balls: 0 };
              undoLastCompletedAtBat();
              setPlayerPosition(null);
              setPitchCounts(pitchCountsToRestore);
              setStep('outcome');
              setReadyForAB(false);
              return;
            }

            if (canUndoBaserunner) {
              const entry = baserunnerHistory[baserunnerHistory.length - 1];
              setBaserunnerHistory(h => h.slice(0, -1));
              setPlayerPosition(entry.prevPosition);
              for (let i = 0; i < entry.actionsToUndo; i++) {
                undoLastBattingAction(pitchCounts);
              }
              return;
            }

            const snap = lastAction.snapshot;
            undoLastBattingAction(pitchCounts);
            if (snap?.atBatPhase === 'active') {
              setPitchCounts(snap.pitchCounts || { swinging: 0, looking: 0, fouls: 0, balls: 0 });
              setPlayerPosition(snap.playerPosition ?? null);
              setStep('outcome');
              setReadyForAB(false);
            } else {
              setPlayerPosition(snap?.playerPosition ?? null);
            }
          }}>
            ↩ Undo Last Action
          </button>
          <button className="pt-undo-last-btn" onClick={() => setShowAtBatLog(true)}>
            📋 View At Bat Log
          </button>
        </div>
      )}
      {showUndoBlocked && (
        <div className="fld-modal-overlay">
          <div className="fld-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="fld-modal-title">Nothing to Undo</div>
            <div className="fld-modal-body">No actions to undo for this at-bat. To fix a previous at-bat, use the At Bat Log.</div>
            <div className="fld-modal-actions">
              <button className="fld-modal-confirm" onClick={() => setShowUndoBlocked(false)}>Got It</button>
              <button className="fld-modal-cancel" onClick={() => { setShowUndoBlocked(false); setShowAtBatLog(true); }}>Open At Bat Log</button>
            </div>
          </div>
        </div>
      )}
      {/* ── closing tag already exists below — do not add another */}
    </div>
  );
}

// ─────────────────────────────────────────────
// STATUS TOGGLE — Starting / Substitute
// ─────────────────────────────────────────────

function StatusToggle({ game }) {
  const { state, setBattingStatus } = game;
  const { batting } = state;
  const [showSub, setShowSub] = useState(false);
  const [subInning, setSubInning] = useState('');

  function handleSubConfirm() {
    setBattingStatus('Substitute', subInning);
    setShowSub(false);
  }

  return (
    <div className="bat-status-wrap">
      <button
        className={`bat-status-pill ${batting.status === 'Starting' ? 'starting' : 'sub'}`}
        onClick={() => {
          if (batting.status === 'Starting') setShowSub(true);
          else setBattingStatus('Starting', '');
        }}
      >
        {batting.status === 'Starting'
          ? 'Starter'
          : `Sub (inn ${batting.substituteInning || '?'})`}
      </button>

      {showSub && (
        <div className="bat-sub-popover">
          <input
            type="number"
            min="1"
            max="14"
            placeholder="Inning entered"
            value={subInning}
            onChange={(e) => setSubInning(e.target.value)}
            className="bat-sub-input"
            autoFocus
          />
          <button className="bat-sub-confirm" onClick={handleSubConfirm}>
            OK
          </button>
          <button className="bat-sub-cancel" onClick={() => setShowSub(false)}>
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function formatOutcome(outcome, strikeoutType) {
  if (outcome === 'k') {
    if (strikeoutType === 'swinging') return 'Struck Out Swinging';
    if (strikeoutType === 'looking') return 'Struck Out Looking';
    return 'Strikeout';
  }
  const map = {
    single: 'Single',
    double: 'Double',
    triple: 'Triple',
    hr: 'Home Run',
    iphr: 'Inside Park HR',
    out: 'Out',
    gidp: 'Double Play',
    bb: 'Walk',
    ibb: 'Int. Walk',
    hbp: 'Hit by Pitch',
    sf: 'Sac Fly',
    sh: 'Sac Bunt',
    roe: 'Reached on Error',
    fc: "Fielder's Choice",
    d3k_reached: 'Dropped 3rd Strike — Safe',
    d3k_out: 'Dropped 3rd Strike — Out',
    ci: "Catcher's Interference",
    run: 'Run Scored',
    sb: 'Stolen Base',
    cs: 'Caught Stealing',
    pickedOff: 'Picked Off',
    fc_out: 'Out on Fielder\'s Choice',
    thrown_out: 'Thrown Out',
    failed_tag_up: 'Failed Tag Up',
    advanced_on_hit: 'Advanced on Hit',
    advanced_on_error: 'Advanced on Error',
    advanced_on_wp_pb: 'Advanced on Wild Pitch / PB',
  };
  return map[outcome] || outcome;
}
